import { useEffect, useState } from "react";
import { RunSheet } from "@/components/run/RunSheet";
import { useOfficialWrite, type OfficialSheetProps } from "./shared";
interface Anchor {
  mode: "none" | "judge1" | "panel_agree";
  heat_rider_id: string | null;
  value: string | null;
  agreed: boolean;
  version: number;
  riders: { heat_rider_id: string; name: string; entries: Record<string, string> }[];
}
export function AnchorSheet(props: OfficialSheetProps & { initialHeatRiderId?: string }) {
  const { heat, competitionId, transport, onClose } = props;
  const base = `/api/competitions/${competitionId}/heats/${heat.id}/anchor`;
  const [state, setState] = useState<Anchor | null>(null);
  const [rider, setRider] = useState("");
  const [value, setValue] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loadError, setLoadError] = useState("");
  const { busy, error, write, execute, undo } = useOfficialWrite(props);
  useEffect(() => {
    let active = true;
    void transport
      .get<Anchor>(base)
      .then((s) => {
        if (active) {
          setState(s);
          setRider(
            props.initialHeatRiderId &&
              s.riders.some((row) => row.heat_rider_id === props.initialHeatRiderId)
              ? props.initialHeatRiderId
              : (s.heat_rider_id ?? s.riders[0]?.heat_rider_id ?? ""),
          );
          setValue(s.value == null ? "" : String(s.value));
          setAgreed(s.agreed);
        }
      })
      .catch((e) => setLoadError(e.message));
    return () => {
      active = false;
    };
  }, [base, transport, props.initialHeatRiderId]);
  const current = state?.riders.find((r) => r.heat_rider_id === rider);
  const benchmark = state?.mode === "judge1" ? String(current?.entries["1"] ?? "") : value;
  return (
    <RunSheet title="Anchor / benchmark score" close={onClose}>
      {(error || loadError) && <p role="alert">{error || loadError}</p>}
      {undo}
      {state && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void write(() =>
              execute("anchor", {
                heat_rider_id: rider,
                value: benchmark,
                agreed,
                expected_version: state.version,
              }),
            );
          }}
        >
          <label>
            Rider
            <select
              value={rider}
              onChange={(e) => {
                setRider(e.target.value);
                setAgreed(false);
              }}
            >
              {state.riders.map((r) => (
                <option key={r.heat_rider_id} value={r.heat_rider_id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <dl>
            {Array.from({ length: heat.judge_count }, (_, i) => i + 1).map((j) => (
              <div key={j}>
                <dt>Judge {j}</dt>
                <dd>{current?.entries[String(j)] ?? "No entry yet"}</dd>
              </div>
            ))}
          </dl>
          <label>
            Benchmark score
            <input
              required
              type="number"
              step="any"
              min="0"
              value={benchmark}
              readOnly={state.mode === "judge1"}
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
          {state.mode === "panel_agree" && (
            <label className="run-toggle">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              Agreed by the panel
            </label>
          )}
          <button
            className="run-primary"
            disabled={busy || !rider || benchmark === "" || state.mode === "none"}
          >
            Save benchmark
          </button>
        </form>
      )}
    </RunSheet>
  );
}
